import { Box, Paper, Tab, Tabs } from "@mui/material";
import i18next from "i18next";
import React, { useCallback, useContext } from "react";
import useStyles from "style/js-style/containers/dashboard/TabStyle";
import { useHistory } from "react-router-dom";
import { Context } from "states/Store";
import { useQuery } from "config/Config";
import ROUTES from "config/Routes";
import clsx from "clsx";
import EATabPanel from "components/common/EATabPanel";
import TabIndued from "./indued/TabIndued";
import { EAChartFilters } from "components/common/charts/EAChartFilters";
import { PrivilegeEnum, RoleBasedAccessControlContext } from "services/common/RolesUtils";
import _ from "lodash";
import { hasOption } from "services/common/Utils";
import TabTransparency from "./transparency/TabTransparency";
import { EANoData } from "components/common/EANoData";
import useSelectedAccountId from "components/common/hooks/useSelectedAccountId";
import TabFinancialStrength from "./financial-strength/TabFinancialStrength";
import TabDashboardEsg from "./esg/TabDashboardEsg";
import TabDashboardEcovadis from "./ecovadis/TabDashboardEcovadis";
import TabDashboardSecurityRating from "./security-rating/TabDashboardSecurityRating";
import TabDashboardCreditsafe from "./credisafe/TabDashboardCreditsafe";

function a11yProps(tab) {
  return {
    id: `${tab}-tab`,
    "aria-controls": `${tab}-tabpanel`,
  };
}

const getRisks = () => {
  return [
    {
      key: "indued",
      sub: "indued",
      requiredPrivileges: [PrivilegeEnum.PRIVILEGE_VIEW_INDUED_SOURCE],
      component: <TabIndued />
    },
    {
      key: "transparency",
      sub: "transparency",
      requiredPrivileges: [PrivilegeEnum.PRIVILEGE_VIEW_TRANSPARENCY_SOURCE],
      component: <TabTransparency />
    },
    {
      key: "financialStrength",
      sub: "financialStrength",
      requiredPrivileges: [PrivilegeEnum.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE],
      component: <TabFinancialStrength />
    },
    {
      key: "esg",
      sub: "esg",
      requiredPrivileges: [PrivilegeEnum.PRIVILEGE_VIEW_DNB_ESG_SOURCE],
      component: <TabDashboardEsg />
    },
    {
      key: "ecovadis",
      sub: "ecovadis",
      requiredPrivileges: [PrivilegeEnum.PRIVILEGE_VIEW_ECOVADIS_SOURCE],
      component: <TabDashboardEcovadis />
    },
    {
      key: "securityRating",
      sub: "security_rating",
      requiredPrivileges: [PrivilegeEnum.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE],
      component: <TabDashboardSecurityRating />
    },
    {
      key: "creditsafe",
      sub: "creditsafe",
      requiredPrivileges: [PrivilegeEnum.PRIVILEGE_VIEW_CREDITSAFE_SOURCE],
      component: <TabDashboardCreditsafe />
    }
  ];
}

function TabRisks() {
  const { classes } = useStyles();
  const [value, setValue] = React.useState(
    parseInt(useQuery().get("risk")) || 0
  );
  const [risksToDisplay, setRisksToDisplay] = React.useState([]);
  const [state] = useContext(Context);
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);
  const history = useHistory();
  const accountId = useSelectedAccountId();

  const handleChange = (event, newValue) => {
    const tabIndex = event.target.tabIndex;
    setValue(tabIndex);
    history.push(`/${accountId}/${ROUTES.DASHBOARD}?tab=3&risk=${tabIndex}`);
  };

  React.useEffect(() => {
    let risks = getRisks().filter(risk => hasOption(state.accounts, state.account.id, risk.sub))
      .map((risk, i) => {
        return {
          ...risk,
          id: i
          // we add id as index, depending on the risks to display
        }
      })
    setRisksToDisplay(risks);
  }, [state.accounts, state.account.id]);

  const isRiskDisabled = useCallback((key) => {
    const riskEntry = _.find(getRisks(), { key: key });

    return riskEntry ? !hasAnyOfPrivileges(...riskEntry?.requiredPrivileges) : true;
  }, [hasAnyOfPrivileges]);

  return (
    <>
      <Paper elevation={0} className={classes.paperContainer}>
        <Box className={classes.tabsWrapper}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="dashboard risks tabs"
            style={{
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
              position: "relative",
              top: "-4px",
            }}
          >
            {risksToDisplay.map((tab) =>
              <Tab
                key={tab.key}
                tabIndex={tab.id}
                label={i18next.t(`dashboard.risks.${tab.key}`)}
                disabled={isRiskDisabled(tab.key)}
                {...a11yProps(tab.key)}
                className={clsx(
                  classes.tab,
                  classes.dt,
                  value === tab.id
                    ? classes.selectedTab
                    : classes.unselectedTab
                )}
              />
            )}
          </Tabs>
        </Box>

        {risksToDisplay.map((tab) => {
          if (!isRiskDisabled(tab.key)) {
            return (
              <EATabPanel key={tab.key} value={value} index={tab.id}>
                {tab.component}
              </EATabPanel>
            )
          } else {
            return (
              <EATabPanel key={tab.key} value={value} index={tab.id}>
                <EANoData key={tab.key}
                  style={{ width: '100%', height: '100%', minHeight: '400px', marginBottom: '30px', background: 'white' }}
                  label={i18next.t("dashboard.risks.unauthorized")}
                  icon="dangerous_grey_light"
                />
              </EATabPanel>
            )
          }
        }
        )}
      </Paper>

      {/* Show ChartFilters if any of the risk tabs are shown */}
      {hasAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_VIEW_INDUED_SOURCE,
        PrivilegeEnum.PRIVILEGE_VIEW_TRANSPARENCY_SOURCE,
        PrivilegeEnum.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE,
        PrivilegeEnum.PRIVILEGE_VIEW_DNB_ESG_SOURCE,
        PrivilegeEnum.PRIVILEGE_VIEW_ECOVADIS_SOURCE,
        PrivilegeEnum.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE,
        PrivilegeEnum.PRIVILEGE_VIEW_CREDITSAFE_SOURCE
      ) && (
          <EAChartFilters tab="risks" subTab={risksToDisplay[value]?.key} />
        )}
    </>
  );
}

export default TabRisks;
